/**
 * Liest Kontoauszug-Dateien externer Quellen ein und wandelt sie in vorbereitete
 * Buchungen (mit Kategorie-Vorschlag) um. Start: TradeRepublic-CSV. Weitere Quellen
 * lassen sich als eigener Parser ergaenzen und in IMPORT_QUELLEN registrieren.
 * Reine Logik ohne Redux/React – der Import-Dialog nutzt nur parseImport().
 */

import { TYP_AUSGABE, TYP_EINNAHME } from './konstanten';
import { kategorieFuerAusgabe } from './mccMapping';

// Auswaehlbare Quellen im Import-Dialog (jede Quelle hat ihr eigenes Format).
export const IMPORT_QUELLEN = [
  { id: 'traderepublic-csv', label: 'TradeRepublic – Kontoauszug (CSV)', dateiTyp: '.csv' },
  { id: 'sparkasse-camt', label: 'Sparkasse – Umsätze (CSV-CAMT)', dateiTyp: '.csv' },
];

/**
 * Dekodiert eine hochgeladene Datei als Text. Versucht zuerst striktes UTF-8 und
 * faellt bei ungueltigen Sequenzen auf Windows-1252 (z. B. Sparkasse-CSV mit
 * Umlauten) zurueck.
 */
export function dekodiere(buffer) {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
  } catch {
    return new TextDecoder('windows-1252').decode(buffer);
  }
}

// Menschenlesbare Spaltennamen fuer die Roh-Ansicht (rechte Seite im Dialog).
const SPALTEN_LABEL = {
  datetime: 'Zeitpunkt', date: 'Datum', account_type: 'Kontotyp', category: 'Kategorie (TR)',
  type: 'Buchungsart', asset_class: 'Anlageklasse', name: 'Name/Händler', symbol: 'Symbol',
  shares: 'Stück', price: 'Preis', amount: 'Betrag', fee: 'Gebühr', tax: 'Steuer',
  currency: 'Währung', original_amount: 'Originalbetrag', original_currency: 'Originalwährung',
  fx_rate: 'Wechselkurs', description: 'Beschreibung', transaction_id: 'Transaktions-ID',
  counterparty_name: 'Gegenpartei', counterparty_iban: 'IBAN', payment_reference: 'Verwendungszweck',
  mcc_code: 'MCC-Code',
};

/** Zerlegt CSV-Text in Zeilen aus Feldern (RFC-4180: Quotes, verdoppelte Quotes, Trennzeichen). */
function parseCsv(text, trenner = ',') {
  const zeilen = [];
  let reihe = [];
  let feld = '';
  let inQuote = false;
  const feldAbschliessen = () => { reihe.push(feld); feld = ''; };
  const reiheAbschliessen = () => { feldAbschliessen(); zeilen.push(reihe); reihe = []; };
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (inQuote) {
      if (c === '"' && text[i + 1] === '"') { feld += '"'; i += 1; }
      else if (c === '"') inQuote = false;
      else feld += c;
    } else if (c === '"') inQuote = true;
    else if (c === trenner) feldAbschliessen();
    else if (c === '\n') reiheAbschliessen();
    else if (c !== '\r') feld += c;
  }
  if (feld !== '' || reihe.length > 0) reiheAbschliessen();
  return zeilen.filter((r) => r.some((f) => f !== ''));
}

/** Mehrfach-Leerzeichen in Haendlernamen zu einem zusammenfassen. */
const normalisiere = (text) => (text || '').replace(/\s+/g, ' ').trim();

/** Baut die Roh-Feldliste (Label + Wert) einer Zeile fuer die Kontroll-Ansicht. */
const baueRohFelder = (kopf, felder) =>
  kopf
    .map((spalte, idx) => ({ label: SPALTEN_LABEL[spalte] || spalte, wert: felder[idx] || '' }))
    .filter((f) => f.wert !== '');

/** Kategorie-Vorschlag: Ausgaben per MCC/Name, Zinsen und sonstige Einnahmen pauschal. */
const schlageKategorieVor = (typ, buchungsart, mcc, name) => {
  if (typ === TYP_EINNAHME) return buchungsart === 'INTEREST_PAYMENT' ? 'Zinsen' : 'Sonstiges';
  return kategorieFuerAusgabe(mcc, name);
};

/** Wandelt eine CSV-Datenzeile in eine vorbereitete Buchung (oder null, wenn unbrauchbar). */
function baueZeile(kopf, felder, index) {
  const wert = (spalte) => felder[kopf.indexOf(spalte)] || '';
  const datum = wert('date');
  const betragRoh = Number(wert('amount'));
  if (!datum || !Number.isFinite(betragRoh) || betragRoh === 0) return null;

  const buchungsart = wert('type');
  const name = normalisiere(wert('name')) || normalisiere(wert('description'));
  const typ = betragRoh < 0 ? TYP_AUSGABE : TYP_EINNAHME;
  const transaktionsId = wert('transaction_id');
  return {
    schluessel: transaktionsId || `${wert('datetime')}#${index}`,
    importId: transaktionsId ? `tr:${transaktionsId}` : `tr:${wert('datetime')}:${betragRoh}`,
    typ,
    betrag: Math.round(Math.abs(betragRoh) * 100) / 100,
    kategorie: schlageKategorieVor(typ, buchungsart, wert('mcc_code'), name),
    zahlweg: buchungsart === 'CARD_TRANSACTION' ? 'Karte' : 'Überweisung',
    datum,
    notiz: name,
    aktiv: true,
    rohFelder: baueRohFelder(kopf, felder),
  };
}

/** Parst einen TradeRepublic-CSV-Kontoauszug; sortiert die Buchungen nach Datum aufsteigend. */
function parseTradeRepublicCsv(text) {
  const zeilen = parseCsv(text);
  if (zeilen.length < 2) throw new Error('Die CSV enthält keine Buchungen.');
  const kopf = zeilen[0].map((s) => s.trim());
  if (!kopf.includes('date') || !kopf.includes('amount')) {
    throw new Error('Unerwartetes Format – ist das ein TradeRepublic-CSV-Export?');
  }
  return zeilen
    .slice(1)
    .map((felder, idx) => baueZeile(kopf, felder, idx))
    .filter(Boolean)
    .sort((a, b) => a.datum.localeCompare(b.datum));
}

// ── Sparkasse CSV-CAMT ──────────────────────────────────

/** Wandelt ein deutsches Datum (TT.MM.JJ oder TT.MM.JJJJ) in ISO (JJJJ-MM-TT). */
function parseDeutschesDatum(roh) {
  const treffer = String(roh).trim().match(/^(\d{2})\.(\d{2})\.(\d{2}|\d{4})$/);
  if (!treffer) return '';
  const [, tag, monat, jahr] = treffer;
  return `${jahr.length === 2 ? `20${jahr}` : jahr}-${monat}-${tag}`;
}

/** Wandelt einen deutschen Geldbetrag ("1.234,56", "-5,00") in eine Zahl. */
function parseDeutscherBetrag(roh) {
  const bereinigt = String(roh).replace(/\./g, '').replace(',', '.').replace(/[^\d.-]/g, '');
  const zahl = Number(bereinigt);
  return Number.isFinite(zahl) ? zahl : NaN;
}

/** Findet den Spaltenindex zu einem der moeglichen Header-Namen (case-insensitiv). */
const spaltenIndex = (kopf, ...namen) =>
  kopf.findIndex((s) => namen.some((n) => s.toLowerCase().replace(/\s+/g, '') === n));

/** Kategorie-Vorschlag fuer eine Sparkasse-Zeile aus Buchungstext + Verwendungszweck. */
const sparkasseKategorie = (typ, buchungstext, text) => {
  if (typ === TYP_EINNAHME) {
    if (/lohn|gehalt|bezuege|bezüge/i.test(`${buchungstext} ${text}`)) return 'Gehalt';
    if (/zins/i.test(`${buchungstext} ${text}`)) return 'Zinsen';
    return 'Sonstiges';
  }
  return kategorieFuerAusgabe(null, text);
};

/** Leitet den Zahlweg aus dem Sparkasse-Buchungstext ab. */
const sparkasseZahlweg = (buchungstext) => {
  const t = (buchungstext || '').toLowerCase();
  if (/karte|kartenzahlung|debitk|visa|girocard/.test(t)) return 'Karte';
  if (/bargeld|auszahlung|geldautomat|gaa/.test(t)) return 'Bar';
  return 'Überweisung';
};

/** Parst einen Sparkasse-CSV-CAMT-Export (Semikolon-getrennt, deutsche Formate). */
function parseSparkasseCamt(text) {
  const zeilen = parseCsv(text, ';');
  if (zeilen.length < 2) throw new Error('Die CSV enthält keine Buchungen.');
  const kopf = zeilen[0].map((s) => s.trim().replace(/^﻿/, ''));
  const idxDatum = spaltenIndex(kopf, 'buchungstag');
  const idxBetrag = spaltenIndex(kopf, 'betrag');
  if (idxDatum < 0 || idxBetrag < 0) {
    throw new Error('Unerwartetes Format – ist das ein Sparkasse-CSV-CAMT-Export?');
  }
  const idx = {
    idxDatum,
    idxBetrag,
    idxZweck: spaltenIndex(kopf, 'verwendungszweck'),
    idxPartner: spaltenIndex(kopf, 'beguenstigter/zahlungspflichtiger', 'begünstigter/zahlungspflichtiger'),
    idxText: spaltenIndex(kopf, 'buchungstext'),
  };
  const zaehler = new Map();
  return zeilen
    .slice(1)
    .map((felder, index) => baueSparkasseZeile(kopf, felder, index, zaehler, idx))
    .filter(Boolean)
    .sort((a, b) => a.datum.localeCompare(b.datum));
}

/** Wandelt eine Sparkasse-Datenzeile in eine vorbereitete Buchung (oder null). */
function baueSparkasseZeile(kopf, felder, index, zaehler, idx) {
  const datum = parseDeutschesDatum(felder[idx.idxDatum]);
  const betragRoh = parseDeutscherBetrag(felder[idx.idxBetrag]);
  if (!datum || !Number.isFinite(betragRoh) || betragRoh === 0) return null;

  const partner = normalisiere(idx.idxPartner >= 0 ? felder[idx.idxPartner] : '');
  const zweck = normalisiere(idx.idxZweck >= 0 ? felder[idx.idxZweck] : '');
  const buchungstext = idx.idxText >= 0 ? felder[idx.idxText] : '';
  const typ = betragRoh < 0 ? TYP_AUSGABE : TYP_EINNAHME;

  // Stabiler Duplikat-Schluessel; identische Zeilen desselben Auszugs zusaetzlich durchnummerieren.
  const basis = `sk:${datum}:${betragRoh}:${partner}:${zweck}`;
  const n = (zaehler.get(basis) || 0) + 1;
  zaehler.set(basis, n);
  const importId = n > 1 ? `${basis}#${n}` : basis;

  return {
    schluessel: `${importId}@${index}`,
    importId,
    typ,
    betrag: Math.round(Math.abs(betragRoh) * 100) / 100,
    kategorie: sparkasseKategorie(typ, buchungstext, `${partner} ${zweck}`),
    zahlweg: sparkasseZahlweg(buchungstext),
    datum,
    notiz: partner || zweck,
    aktiv: true,
    rohFelder: baueRohFelder(kopf, felder),
  };
}

/** Parst eine hochgeladene Datei anhand der gewaehlten Quelle. */
export function parseImport(quelleId, text) {
  if (quelleId === 'traderepublic-csv') return parseTradeRepublicCsv(text);
  if (quelleId === 'sparkasse-camt') return parseSparkasseCamt(text);
  throw new Error('Unbekannte Import-Quelle.');
}
